import React, { useState, useEffect } from 'react';
import CalcShell from '../CalcShell';
import { CNetActions, CNetResult, CNetHintBar, G } from '../other/_cnet';

// Half-Life = ln(2)/λ, meanLife T = 1/λ
export default function HalfLifeCalc() {
  return (
    <CalcShell showActions={false} look="cnet" title="Half-Life Calculator" subtitle="Compute quantity remaining, time elapsed, or half-life from any three of the four variables. Also convert between half-life, mean lifetime, and decay constant.">
      <p className="text-sm text-slate-700 mb-3">The following tools can generate any one of the values from the other three in the half-life formula for a substance undergoing decay to decrease by half.</p>
      <CNetHintBar />
      <HalfLifeSolver />
      <ConversionSolver />
    </CalcShell>
  );
}

function HalfLifeSolver() {
  const [Nt, setNt] = useState(10);
  const [N0, setN0] = useState(100);
  const [t, setT] = useState(50);
  const [half, setHalf] = useState('');
  const [res, setRes] = useState(null);
  const calc = () => {
    // solve whichever is empty
    const _Nt = Nt === '' ? null : Number(Nt);
    const _N0 = N0 === '' ? null : Number(N0);
    const _t = t === '' ? null : Number(t);
    const _half = half === '' ? null : Number(half);
    const known = [_Nt, _N0, _t, _half].filter(v => v != null && !isNaN(v)).length;
    if (known < 3) { setRes({ error: 'Provide any three of the four values.' }); return; }
    let out = {};
    if (_half == null) out = { field: 'half-life', value: (_t * Math.log(2)) / Math.log(_N0 / _Nt), unit: 'time units' };
    else if (_Nt == null) out = { field: 'quantity remaining', value: _N0 * Math.pow(0.5, _t / _half), unit: '' };
    else if (_N0 == null) out = { field: 'initial quantity', value: _Nt / Math.pow(0.5, _t / _half), unit: '' };
    else if (_t == null) out = { field: 'time', value: _half * Math.log(_N0 / _Nt) / Math.log(2), unit: 'time units' };
    setRes({ out, Nt: _Nt, N0: _N0, t: _t, half: _half });
  };
  const clear = () => { setNt(10); setN0(100); setT(50); setHalf(''); setRes(null); };
  useEffect(() => { calc(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [Nt, N0, t, half]);
  return (
    <>
      <h3 className="cnet-section-title">Half-Life Calculator</h3>
      <p className="text-sm text-slate-700 mb-2">Please provide any three of the following to calculate the fourth value.</p>
      <div className="cnet-panel">
        <div className="grid grid-cols-4 gap-4">
          <div><div className="text-center text-slate-700 text-sm mb-1">quantity remains <b>N<sub>t</sub></b></div>
            <input data-testid="hl-nt" className="cnet-input w-full" type="number" value={Nt} onChange={e => setNt(e.target.value)} /></div>
          <div><div className="text-center text-slate-700 text-sm mb-1">initial quantity <b>N<sub>0</sub></b></div>
            <input data-testid="hl-n0" className="cnet-input w-full" type="number" value={N0} onChange={e => setN0(e.target.value)} /></div>
          <div><div className="text-center text-slate-700 text-sm mb-1">time <b>t</b></div>
            <input data-testid="hl-t" className="cnet-input w-full" type="number" value={t} onChange={e => setT(e.target.value)} /></div>
          <div><div className="text-center text-slate-700 text-sm mb-1">half-life <b>t<sub>1/2</sub></b></div>
            <input data-testid="hl-half" className="cnet-input w-full" type="number" value={half} onChange={e => setHalf(e.target.value)} /></div>
        </div>
      </div>
      {res && res.error && <div className="text-red-600 mt-2">{res.error}</div>}
      {res && !res.error && (
        <CNetResult>
          <div className="text-2xl font-semibold text-slate-900" data-testid="hl-result">
            {res.out.field}: <span className="text-[#4c7b25]">{G(res.out.value, 6)}</span> {res.out.unit}
          </div>
          <div className="mt-3 font-bold text-slate-900">Steps:</div>
          <div className="ml-2 font-mono text-sm leading-7">
            <div>N(t) = N₀ · (½)^(t / t<sub>½</sub>)</div>
            <div>{res.Nt} = {res.N0} · (½)^({res.t} / {res.half || '?'})</div>
            <div>= {res.N0} · (½)^{res.half ? G(res.t / res.half, 4) : '?'}</div>
            <div>{res.out.field} = <b className="text-[#4c7b25]">{G(res.out.value, 6)}</b></div>
          </div>
        </CNetResult>
      )}
    </>
  );
}

function ConversionSolver() {
  const [h, setH] = useState('');
  const [tau, setTau] = useState('');
  const [lam, setLam] = useState('');
  const [res, setRes] = useState(null);
  const calc = () => {
    // any one: derive the other two. Half-life = ln(2)/λ = ln(2)·τ
    const _h = h === '' ? null : Number(h);
    const _tau = tau === '' ? null : Number(tau);
    const _lam = lam === '' ? null : Number(lam);
    let H, T, L;
    if (_h != null) { H = _h; L = Math.log(2) / _h; T = 1 / L; }
    else if (_tau != null) { T = _tau; L = 1 / _tau; H = Math.log(2) * _tau; }
    else if (_lam != null) { L = _lam; H = Math.log(2) / _lam; T = 1 / _lam; }
    else { setRes({ error: 'Provide any one value.' }); return; }
    setRes({ H, T, L });
  };
  const clear = () => { setH(''); setTau(''); setLam(''); setRes(null); };
  return (
    <>
      <h3 className="cnet-section-title mt-4">Half-Life, Mean Lifetime, and Decay Constant Conversion</h3>
      <p className="text-sm text-slate-700 mb-2">Please provide any one of the following to get the other two.</p>
      <div className="cnet-panel">
        <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 items-end">
          <div><div className="text-center text-slate-700 text-sm mb-1">half-life <b>t<sub>1/2</sub></b></div>
            <input data-testid="hlc-h" className="cnet-input w-full" type="number" value={h} onChange={e => setH(e.target.value)} /></div>
          <div className="text-xl font-bold pb-2">=</div>
          <div><div className="text-center text-slate-700 text-sm mb-1">mean lifetime <b>τ</b></div>
            <input data-testid="hlc-tau" className="cnet-input w-full" type="number" value={tau} onChange={e => setTau(e.target.value)} /></div>
          <div className="text-xl font-bold pb-2">=</div>
          <div><div className="text-center text-slate-700 text-sm mb-1">decay constant <b>λ</b></div>
            <input data-testid="hlc-lam" className="cnet-input w-full" type="number" value={lam} onChange={e => setLam(e.target.value)} /></div>
        </div>
        <CNetActions onCalc={calc} onClear={clear} testId="hlc" />
      </div>
      {res && res.error && <div className="text-red-600 mt-2">{res.error}</div>}
      {res && !res.error && (
        <CNetResult>
          <table className="cnet-info-table" data-testid="hlc-result">
            <tbody>
              <tr><td>Half-life t<sub>1/2</sub>:</td><td><b className="text-[#4c7b25]">{G(res.H, 6)}</b></td></tr>
              <tr><td>Mean lifetime τ:</td><td><b className="text-[#4c7b25]">{G(res.T, 6)}</b></td></tr>
              <tr><td>Decay constant λ:</td><td><b className="text-[#4c7b25]">{G(res.L, 6)}</b></td></tr>
            </tbody>
          </table>
        </CNetResult>
      )}
    </>
  );
}
